import React, { type ReactElement } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import type { GlobalHotKey } from '.';

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');

const getKeyLabels = (hotKey: GlobalHotKey): string[] =>
  [
    hotKey.ctrlKey && (isMac ? '⌃' : 'Ctrl'),
    hotKey.altKey && (isMac ? '⌥' : 'Alt'),
    hotKey.shiftKey && (isMac ? '⇧' : 'Shift'),
    hotKey.metaKey && (isMac ? '⌘' : 'Win'),
    hotKey.code.replace(/^(Key|Digit)/, ''),
  ].filter((label): label is string => Boolean(label));

type GlobalHotKeysHelpProps = {
  hotKeys: GlobalHotKey[];
};

const GlobalHotKeysHelp = ({ hotKeys }: GlobalHotKeysHelpProps): ReactElement => (
  <Paper
    role='region'
    aria-label='Keyboard shortcuts'
    elevation={12}
    sx={(theme) => ({
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: theme.zIndex.tooltip,
      pointerEvents: 'none',
      minWidth: 320,
      maxWidth: `calc(100vw - ${theme.spacing(4)})`,
      padding: theme.spacing(3),
      borderRadius: `${theme.shape.radius.lg}px`,
      backgroundColor: alpha(theme.palette.background.paper, 0.7),
      backdropFilter: 'blur(1px)',
    })}
  >
    <Typography variant='titleMedium' component='h2' sx={{ marginBottom: 2 }}>
      Keyboard shortcuts
    </Typography>
    <Stack component='ul' gap={1.5} sx={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {hotKeys.map((hotKey) => (
        <Stack
          component='li'
          key={`${hotKey.code}:${hotKey.description}`}
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          gap={4}
        >
          <Typography variant='paragraphRegular'>{hotKey.description}</Typography>
          <Stack direction='row' gap={0.5}>
            {getKeyLabels(hotKey).map((label) => (
              <Box
                component='kbd'
                key={label}
                sx={(theme) => ({
                  ...theme.typography.captionMedium,
                  minWidth: 24,
                  padding: theme.spacing(0.25, 0.75),
                  border: `1px solid ${theme.palette.border.light}`,
                  borderRadius: `${theme.shape.radius.sm}px`,
                  backgroundColor: alpha(theme.palette.background.default, 0.6),
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                })}
              >
                {label}
              </Box>
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  </Paper>
);

export default GlobalHotKeysHelp;
